import * as React from 'react';
import { cn } from '@/lib/utils/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  minRows?: number;
  maxRows?: number;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, minRows = 1, maxRows = 10, ...props }, forwardedRef) => {
    const [textareaRef, setTextareaRef] = React.useState<HTMLTextAreaElement | null>(null);

    const adjustTextareaHeight = () => {
      if (textareaRef) {
        const textarea = textareaRef;
        textarea.style.height = 'auto';
        const computedStyle = window.getComputedStyle(textarea);
        const lineHeight = parseInt(computedStyle.lineHeight || '0');
        const paddingTop = parseInt(computedStyle.paddingTop || '0');
        const paddingBottom = parseInt(computedStyle.paddingBottom || '0');
        const totalPadding = paddingTop + paddingBottom;

        const scrollHeight = textarea.scrollHeight - totalPadding;
        const rows = Math.min(maxRows, Math.max(minRows, Math.floor(scrollHeight / lineHeight)));
        textarea.style.height = `${rows * lineHeight}px`;
      }
    };

    React.useEffect(() => {
      adjustTextareaHeight();
    }, [props.value, minRows, maxRows]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const textarea = event.target as HTMLTextAreaElement;
      const lines = textarea.value.split('\n').length;
      if (lines >= maxRows && event.key !== 'Backspace' && event.key !== 'Delete') {
        event.preventDefault();
      }
    };

    const refCallback = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        if (typeof forwardedRef === 'function') {
          if (node !== null) {
            forwardedRef(node);
          }
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
        setTextareaRef(node);
      },
      [forwardedRef]
    );

    return (
      <textarea
        className={cn(
          'flex min-h-[60px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm placeholder:text-text placeholder:opacity-[0.3] placeholder:text-[12px] placeholder:font-normal focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-1 disabled:bg-disableBg disabled:text-disableText disabled:cursor-not-allowed',
          className
        )}
        ref={refCallback}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  }
);

Textarea.displayName = 'Textarea';

export { Textarea };
